import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { MfaGate } from "@/features/auth/MfaGate";

/**
 * Wraps routes that require authentication.
 * - If we don't know yet (initial session check): render nothing briefly
 * - If signed out: redirect to /login (remembering where they were going)
 * - If signed in: render the protected content behind the two-factor gate
 *   (AUTH-MFA-01: a code prompt when the account has an authenticator, or a
 *   required enrollment when the org demands it for admins)
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <MfaGate>{children}</MfaGate>;
}
